import cx from 'clsx';
import React, { useState } from 'react';
import { useController, useForm, useWatch } from 'react-hook-form';

import { ReactComponent as EditIcon } from '@assets/svg/ic-edit-pencil.svg';
import { ReactComponent as SaveIcon } from '@assets/svg/ic-save.svg';

import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { ISensorListEquipment } from '@pages/SensorsList/model/sensorListEquipment';
import { useEquipmentEditAssetCodeMutation } from '@pages/SensorsList/services/useEquipmentEditAssetCodeMutation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import style from './style.module.scss';

interface IProps {
  equipment?: ISensorListEquipment;
  locationId?: number | string;
}

const AssetCodeInput: React.FC<IProps> = ({ equipment, locationId }) => {
  const { t } = useTranslation();
  const [isEdit, setIsEdit] = useState(false);

  const { setActiveModal } = useModalActions();

  const { data: profileData } = useUserProfileQuery();
  const { mutate: equipmentEditAssetCodeMutate, isLoading } = useEquipmentEditAssetCodeMutation({
    locationId,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    mode: 'onChange',
    values: {
      asset_code: equipment?.asset_code || '',
      equipment_id: equipment?.id,
    },
  });

  const assetCodeValue = useWatch({ name: 'asset_code', control });

  const submit = ({ isReplaceName, checkCode }: { isReplaceName?: number; checkCode?: number }) =>
    handleSubmit((data) => {
      if (isDirty) {
        equipmentEditAssetCodeMutate(
          { ...data, is_replace_name: isReplaceName, check_code: checkCode },
          {
            onSuccess: () => {
              showToast({ variant: 'success', message: t('Your changes have been saved.') });
              setIsEdit(false);
              reset({
                asset_code: data.asset_code,
                equipment_id: equipment?.id,
              });
            },
          },
        );
      } else {
        showToast({
          variant: 'info',
          message: t('No changes were made.'),
        });
        setIsEdit(false);
      }
    })();

  const showConfirmModal = () => {
    setActiveModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Suggested matching text for Equipment Name and Asset Code'),
        bodyContent: t(
          `The {assetCode} you entered no longer matches the {equipmentName} field. Would you like to update the {equipmentName} field as well?`,
          {
            assetCode: assetCodeValue,
            equipmentName: equipment?.name || '',
          },
        ),
        onConfirm: () => submit({ isReplaceName: 1, checkCode: 0 }),
        onCancel: () => submit({ isReplaceName: 0, checkCode: 0 }),
        confirmText: t('Yes'),
        cancelText: t('No'),
      },
    });
  };

  const handleClick = () => {
    if (!isEdit) {
      setIsEdit(true);
      return;
    }

    if (assetCodeValue && equipment?.name?.includes(equipment?.asset_code)) {
      showConfirmModal();
      return;
    }

    submit({});
  };

  return (
    <div className={style.assetCodeInputWrapper}>
      <Input
        name={'asset_code'}
        control={control}
        label={t('Asset code')}
        type={'woHelperText'}
        disabled={!isEdit}
      />
      {(profileData?.role === 'Super Admin' || profileData?.role === 'Administrator') && (
        <Button
          type={'secondary'}
          leftElement={
            isEdit ? <SaveIcon className={style.saveEditIcon} /> : <EditIcon className={style.saveEditIcon} />
          }
          disabled={isLoading}
          onClick={handleClick}
        />
      )}
    </div>
  );
};

export default AssetCodeInput;
